import { useState, useEffect } from 'react'
import { RefreshCw, Loader, CheckCircle2, ChevronRight, Shield, AlertCircle, X, Trash2 } from 'lucide-react'
import { useAppStore }    from '../store/useAppStore'
import { useAdminStore }  from '../store/useAdminStore'
import { useLogStore }    from '../store/useLogStore'
import { usePendingMrcStore } from '../store/usePendingMrcStore'
import { apiService }     from '../services/myrentcarApi'
import { OLYMPIC_AGENCIES } from '../utils/agencies'
import { buildAuthHeader } from '../utils/auth'
import Button from '../components/ui/Button'
import Card   from '../components/ui/Card'
import toast  from 'react-hot-toast'

export default function SyncScreen() {
  const { logs, clearLogs } = useLogStore()
  const [showLogs, setShowLogs] = useState(false)

  const { credentials, setContracts, setSelectedAgency, selectedAgency, contracts, setStep } = useAppStore()
  const { isAdmin, checkAdmin } = useAdminStore()
  const cleanupAfterSync = usePendingMrcStore(s => s.cleanupAfterSync)

  const [loading,  setLoading]  = useState(false)
  const [synced,   setSynced]   = useState(false)
  const [count,    setCount]    = useState(contracts?.length || null)
  const [selected, setSelected] = useState(selectedAgency ? [selectedAgency] : [])

  // ── Vérification admin au montage ────────────────────────────────────────
  useEffect(() => {
    if (credentials) {
      const token = buildAuthHeader(credentials)
      checkAdmin(token)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [credentials])

  const toggleAgency = (agencyId: string) => {
    setSelected((prev: string[]) =>
      prev.includes(agencyId) ? prev.filter((id: string) => id !== agencyId) : [...prev, agencyId]
    )
  }

  const handleSync = async () => {
    if (!selected.length) { toast.error('Sélectionnez au moins une agence'); return }
    setLoading(true); setSynced(false)
    const toastId = toast.loading('Synchronisation...')
    try {
      let allContracts: any[] = []
      for (const agId of selected) {
        const res = await apiService.fetchContracts(credentials, agId)
        allContracts = [...allContracts, ...res]
      }
      const unique = Array.from(new Map(allContracts.map(c => [c.id, c])).values())
      const ID_TO_CODE = {
        '111':'00','112':'01','110':'02','113':'03',
        '114':'06','120':'08','121':'09','125':'12',
        '126':'13','124':'PEU'
      }
      const selectedCodes = selected.map((id: string) => (ID_TO_CODE as any)[String(id)] || id)
      const filtered = selectedCodes.length
        ? unique.filter(c => c.agenceDepartCode && selectedCodes.includes(c.agenceDepartCode))
        : unique
      const finalList = filtered.length > 0 ? filtered : unique
      setContracts(finalList)
      
      // ✅ Nettoyage ciblé des retours MRC : uniquement les agences synchronisées
      const syncedAgencyCodes = [...new Set(finalList.map((c: any) => c.agenceDepartCode).filter(Boolean))] as string[];
      cleanupAfterSync(syncedAgencyCodes, finalList.map(c => String(c.rawId ?? c.id)))

      setSelectedAgency(selected[0])
      setCount(finalList.length)
      setSynced(true)
      toast.success(`${unique.length} contrats synchronisés`, { id: toastId })
      // ✅ Auto-navigation vers la liste après sync réussie (800ms pour voir le toast)
      setTimeout(() => setStep('contracts'), 800)
    } catch (err: any) {
      toast.error('Erreur : ' + err.message, { id: toastId })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 flex flex-col gap-4 max-w-2xl mx-auto">

      {/* ── Header avec bouton Admin & Stats ── */}
      <div className="flex items-center justify-between mt-2">
        <h1 className="text-xl font-black text-gray-800">Synchronisation</h1>
        <div className="flex gap-2">
          <button
            onClick={() => setShowLogs(true)}
            className="flex items-center gap-1.5 bg-gray-200 hover:bg-gray-300 text-gray-700 text-xs font-bold px-3 py-2 rounded-xl shadow transition"
          >
            <AlertCircle size={15} className={logs.length > 0 ? "text-red-500" : "text-gray-500"} />
            <span className="hidden sm:inline">Logs</span> {logs.length > 0 && `(${logs.length})`}
          </button>
          {isAdmin && (
            <>
              <button
                onClick={() => setStep('stats')}
                className="flex items-center gap-1.5 bg-sky-600 hover:bg-sky-700 text-white text-xs font-bold px-3 py-2 rounded-xl shadow transition"
              >
                📊 Stats
              </button>
              <button
                onClick={() => setStep('admin')}
                className="flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold px-3 py-2 rounded-xl shadow transition"
              >
                <Shield size={15} />
                Admin
              </button>
            </>
          )}
        </div>
      </div>

      <Card className="p-4">
        <p className="text-sm font-semibold text-gray-600 mb-3">Sélectionnez la ou les agences</p>
        <div className="grid grid-cols-2 gap-2">
          {OLYMPIC_AGENCIES.map(ag => {
            const isActive = selected.includes(ag.id)
            return (
              <button
                key={ag.id}
                onClick={() => toggleAgency(ag.id)}
                className={`flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all ${
                  isActive ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white'
                }`}
              >
                <span className="w-8 h-8 rounded-lg flex-shrink-0" style={{ backgroundColor: ag.color }}/>
                <div className="overflow-hidden flex-1">
                  <p className="text-xs font-black text-gray-800">{ag.code}</p>
                  <p className="text-xs text-gray-500 truncate">{ag.name}</p>
                </div>
                {isActive && <CheckCircle2 size={16} className="text-blue-500 flex-shrink-0"/>}
              </button>
            )
          })}
        </div>
      </Card>

      {count !== null && (
        <Card className={`p-3 flex items-center gap-3 ${synced ? 'bg-green-50 border-green-200' : 'bg-gray-50'}`}>
          <CheckCircle2 size={20} className={synced ? 'text-green-500' : 'text-gray-400'} />
          <p className="text-sm font-semibold text-gray-700">
            {count} contrats disponibles
          </p>
        </Card>
      )}

      <Button onClick={handleSync} disabled={loading || !selected.length} size="xl" className="w-full">
        {loading
          ? <><Loader size={20} className="animate-spin"/> Synchronisation...</>
          : <><RefreshCw size={20}/> Synchroniser</>
        }
      </Button>

      {count > 0 && (
        <Button onClick={() => setStep('contracts')} variant="secondary" size="lg" className="w-full flex items-center justify-center gap-2">
          Voir les {count} contrats <ChevronRight size={18}/>
        </Button>
      )}

      {/* ── Modal des Logs In-App (Diagnostic) ── */}
      {showLogs && (
        <div className="fixed inset-0 z-[100] flex flex-col bg-gray-950/90 backdrop-blur-sm animate-fadeIn">
          <div className="flex items-center justify-between p-4 bg-gray-900 border-b border-gray-800">
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <AlertCircle className="text-blue-400" />
              Console de Diagnostic
            </h2>
            <div className="flex gap-3">
              <button onClick={clearLogs} className="text-gray-400 hover:text-red-400 transition" title="Purger">
                <Trash2 size={24} />
              </button>
              <button onClick={() => setShowLogs(false)} className="text-gray-400 hover:text-white transition">
                <X size={28} />
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-auto p-4 space-y-3">
            {logs.length === 0 ? (
              <p className="text-center text-gray-500 mt-10">Aucun log enregistré.</p>
            ) : (
              logs.map((log: any) => (
                <div key={log.id} className="bg-gray-800 rounded-xl p-3 border-l-4 border-blue-500">
                  <div className="flex justify-between items-start mb-1">
                    <span className={`text-xs font-black uppercase tracking-wider px-2 py-0.5 rounded ${
                      log.type === 'error' ? 'bg-red-500/20 text-red-400' : 'bg-blue-500/20 text-blue-400'
                    }`}>
                      {log.type}
                    </span>
                    <span className="text-[10px] text-gray-500">{new Date(log.timestamp).toLocaleTimeString()}</span>
                  </div>
                  <p className="text-sm font-semibold text-gray-200 mt-1">{log.message}</p>
                  {log.details && (
                    <pre className="mt-2 bg-black/50 p-2 rounded text-[10px] text-gray-400 overflow-x-auto">
                      {log.details}
                    </pre>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}